// ANCHOR React
import { useState } from 'react';

// ANCHOR Base
import { Button } from 'baseui/button';
import { styled } from 'baseui';

// ANCHOR Models
import { useFormMutate, useFormDataID } from '@lyon/scoped-models/form/FormData';
import { useUpdateFormFormSubmitPayload } from '@lyon/scoped-models/form/update/UpdateFormForm';
import { useProviderFormMutate } from '@lyon/scoped-models/form/ProviderFormData';

// ANCHOR API
import { updateFormById } from '@lyon/utils/api/forms';

// ANCHOR Graph State
import { useAuthToken } from '@lyon/nodes/firebase/auth/auth-token-node';

// ANCHOR Hooks
import { useSentryExceptionHandler } from '@lyon/utils/hooks/useSentryExceptionHandler';
import { usePromise } from '@lyon/utils/hooks/usePromise';

// ANCHOR Components
import { constant } from '@lyon/components/utils/constant';

// ANCHOR Styles
import { useConstantCallback } from '@lyon/utils/hooks/useConstantCallback';
import { BUTTON, BLOCK } from './styles';

const HoverableDiv = styled('div', BLOCK);

export const LyonUpdateFormSaveButton = constant(() => {
  const token = useAuthToken();
  const catchException = useSentryExceptionHandler();
  const mutate = useFormMutate();
  const mutateProviderForms = useProviderFormMutate();

  const formId = useFormDataID();
  const { state } = useUpdateFormFormSubmitPayload();
  const {
    title, type, description, scoringPolicy, allowedAttempts, timeLimit,
    accessCode, canGoBack, isLocked, isPractice, oneTimeResult,
    showCorrectResponse, showRankPercentile, showScore, showAfterRegrade,
    shuffleQuestion, textSelection,
  } = state;

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const mounted = usePromise([
    token, title, type, description, scoringPolicy, allowedAttempts,
    timeLimit, accessCode, canGoBack, isLocked, isPractice, oneTimeResult,
    showCorrectResponse, showRankPercentile, showScore, showAfterRegrade,
    shuffleQuestion, textSelection, formId,
  ]);

  const isDisabled = !title || !type || !scoringPolicy;

  const submit = async () => {
    setLoading(true);

    try {
      if (token && formId && title && type && scoringPolicy) {
        await mounted(updateFormById({
          token,
          id: formId,
          title,
          type,
          description,
          scoringPolicy,
          allowedAttempts: allowedAttempts || null,
          timeLimit: timeLimit || null,
          accessCode,
          canGoBack,
          isLocked,
          isPractice,
          oneTimeResult,
          showCorrectResponse,
          showRankPercentile,
          showScore,
          showAfterRegrade,
          shuffleQuestion,
          textSelection,
        }));

        await mounted(mutateProviderForms());
        await mounted(mutate());

        setSuccess(true);
      }
    } catch (error) {
      catchException(error);
    }

    setLoading(false);
  };

  const onMouseEnter = useConstantCallback(() => {
    setSuccess(false);
  });

  return (
    <HoverableDiv onMouseEnter={onMouseEnter}>
      <Button
        type="button"
        overrides={BUTTON}
        onClick={submit}
        isLoading={loading}
        disabled={isDisabled}
      >
        {success ? 'Saved!' : 'Save Settings'}
      </Button>
    </HoverableDiv>
  );
});
